import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Brain, Sigma, Activity, Clock, BarChart3 } from "lucide-react";
import AnimatedNumber from "./AnimatedNumber";
import ProgressRing from "./ProgressRing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const REGIME_COLORS = {
  trending: "#4dffaf",
  ranging:  "#5cf2ff",
  bearish:  "#ff5577",
  bullish:  "#4dffaf",
  volatile: "#ffc24d",
  neutral:  "#94a3b8",
};

function HourBar({ hour, pnl, max, trades }) {
  const pct = Math.min(100, (Math.abs(pnl) / Math.max(1, max)) * 100);
  const isWin = pnl >= 0;
  return (
    <div className="flex flex-col items-center gap-1 group" title={`${hour}:00 · ${trades} trades · ${pnl >= 0 ? "+" : ""}$${pnl.toFixed(0)}`}>
      <div className="relative h-24 w-full flex items-end">
        <div
          className={`w-full rounded-t-sm transition-all duration-700 ${isWin ? "bg-gradient-to-t from-emerald-500/50 to-emerald-300" : "bg-gradient-to-t from-rose-500/50 to-rose-300"} group-hover:opacity-100 opacity-80`}
          style={{ height: `${pct}%` }}
        />
      </div>
      <div className="text-[8px] font-mono tabular-nums text-white/45">{hour}</div>
    </div>
  );
}

export default function StatsArenaPage() {
  const [identity, setIdentity] = useState(null);
  const [perf, setPerf] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [id, p] = await Promise.allSettled([
          axios.get(`${API}/quantum/identity`),
          axios.get(`${API}/analytics/performance`),
        ]);
        if (!alive) return;
        if (id.status === "fulfilled") setIdentity(id.value.data);
        if (p.status === "fulfilled") setPerf(p.value.data);
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const regime = String(identity?.regime || "neutral").toLowerCase();
  const regimeColor = REGIME_COLORS[regime] || REGIME_COLORS.neutral;
  const H = Number(identity?.H || 0);
  const C = Number(identity?.C || 0);
  const epsilon = Number(identity?.epsilon || 0);
  const omega = Number(identity?.omega || 0);
  const nActive = Number(identity?.n_active || 0);

  const hourly = useMemo(() => perf?.by_hour || [], [perf]);
  const maxHour = Math.max(1, ...hourly.map((h) => Math.abs(Number(h.pnl || 0))));
  const bestHour = useMemo(() => {
    if (!hourly.length) return null;
    return [...hourly].sort((a, b) => Number(b.pnl || 0) - Number(a.pnl || 0))[0];
  }, [hourly]);

  return (
    <div className="space-y-5">
      {/* QUANTUM IDENTITY */}
      <section className="arena-equity-card">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Brain className="h-4 w-4 text-violet-300" />
            <h2 className="font-display text-lg font-semibold">Quantum Identity</h2>
          </div>
          <span className="rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-[0.22em]"
                style={{ color: regimeColor, borderColor: `${regimeColor}55`, background: `${regimeColor}11` }}>
            {regime}
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <QuantumOrb label="H · superposition" value={H} max={1} color="#5cf2ff" />
          <QuantumOrb label="C · coherence" value={C} max={1} color="#4dffaf" />
          <QuantumOrb label="ε · environment" value={epsilon} max={1} color="#ffd76a" />
          <QuantumOrb label="Ω · output" value={omega} max={1} color="#b377ff" />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 text-[10px] uppercase tracking-[0.22em]">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-2">
            <div className="text-white/45">active engines</div>
            <div className="mt-1 text-white font-display text-base">
              <AnimatedNumber value={nActive} decimals={0} />
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-2">
            <div className="text-white/45">M · L stack</div>
            <div className="mt-1 text-white font-display text-base">
              {Number(identity?.M || 0).toFixed(2)} / {Number(identity?.L || 0).toFixed(2)}
            </div>
          </div>
        </div>
      </section>

      {/* HOURLY HEATMAP */}
      <section className="arena-equity-card">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-cyan-300" />
            <h2 className="font-display text-lg font-semibold">Hourly Edge</h2>
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">utc</span>
          </div>
          {bestHour ? (
            <span className="text-[10px] uppercase tracking-[0.22em] text-emerald-300">
              best · {bestHour.hour}:00
            </span>
          ) : null}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(24, 1fr)", gap: 2 }}>
          {hourly.length === 0 ? (
            <div className="col-span-full text-center text-white/45 text-[11px] uppercase tracking-[0.22em] py-6">
              no hourly data
            </div>
          ) : null}
          {hourly.map((h) => (
            <HourBar key={h.hour} hour={h.hour} pnl={Number(h.pnl || 0)} trades={Number(h.trades || 0)} max={maxHour} />
          ))}
        </div>
      </section>

      {/* ENGINE ALPHA STREAM */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.32em] text-white/55">Engine α stream</div>
          <Sigma className="h-3 w-3 text-white/45" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {identity?.alphas
            ? Object.entries(identity.alphas).map(([name, alpha]) => {
                const pct = Math.min(100, Math.abs(Number(alpha) || 0) * 4);
                const positive = Number(alpha) >= 0;
                const color = positive ? "#4dffaf" : "#ff5577";
                return (
                  <div key={name} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] mb-1">
                      <span className="text-white/65">{name.replace(/_/g, " ")}</span>
                      <span className="font-mono tabular-nums" style={{ color }}>
                        {Number(alpha).toFixed(2)}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <div className="h-full rounded-full transition-[width] duration-700"
                           style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}, transparent)`, boxShadow: `0 0 8px ${color}` }} />
                    </div>
                  </div>
                );
              })
            : null}
        </div>
      </section>
    </div>
  );
}

function QuantumOrb({ label, value, max = 1, color }) {
  const pct = Math.max(0, Math.min(100, (Number(value) / max) * 100));
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 flex items-center gap-3">
      <ProgressRing pct={pct} size={56} stroke={5} color={color}>
        <div className="text-center">
          <div className="text-sm font-bold tabular-nums">{Number(value).toFixed(2)}</div>
        </div>
      </ProgressRing>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/65 leading-tight">{label}</div>
    </div>
  );
}
